import React, { useEffect, useMemo, useState } from 'react';

export const WEEK_DAYS = [
  { value: 0, short: 'Sun', label: 'Sunday' },
  { value: 1, short: 'Mon', label: 'Monday' },
  { value: 2, short: 'Tue', label: 'Tuesday' },
  { value: 3, short: 'Wed', label: 'Wednesday' },
  { value: 4, short: 'Thu', label: 'Thursday' },
  { value: 5, short: 'Fri', label: 'Friday' },
  { value: 6, short: 'Sat', label: 'Saturday' },
];

const DEFAULT_START = '08:00';
const DEFAULT_END = '17:00';

const compareTime = (left, right) => String(left).localeCompare(String(right));
const maxTime = (left, right) => (compareTime(left, right) >= 0 ? left : right);
const minTime = (left, right) => (compareTime(left, right) <= 0 ? left : right);

export const normalizeWorkDaysHours = (rows) =>
  (Array.isArray(rows) ? rows : [])
    .map((row) => ({
      day_of_week: Number(row.day_of_week ?? row.dayOfWeek ?? row.day),
      shift_start: String(row.shift_start ?? row.shiftStart ?? DEFAULT_START).slice(0, 5),
      shift_end: String(row.shift_end ?? row.shiftEnd ?? DEFAULT_END).slice(0, 5),
    }))
    .filter((row) => Number.isInteger(row.day_of_week) && row.day_of_week >= 0 && row.day_of_week <= 6)
    .sort((a, b) => a.day_of_week - b.day_of_week);

export const workDaysHoursLabel = (rows, fallback = 'All days, 24 hours') => {
  const normalized = normalizeWorkDaysHours(rows);
  if (normalized.length === 0) return fallback;
  return normalized
    .map((row) => {
      const day = WEEK_DAYS.find((item) => item.value === row.day_of_week);
      return `${day?.short || row.day_of_week} ${row.shift_start}-${row.shift_end}`;
    })
    .join(', ');
};

const WorkDaysHoursPicker = ({
  value,
  onChange,
  mode = 'edit',
  availableRows,
  labels = {},
}) => {
  const rows = useMemo(() => normalizeWorkDaysHours(value), [value]);
  const available = useMemo(() => normalizeWorkDaysHours(availableRows), [availableRows]);
  const availableByDay = useMemo(
    () => new Map(available.map((row) => [row.day_of_week, row])),
    [available]
  );
  const selectedDays = new Set(rows.map((row) => row.day_of_week));
  const [sameShift, setSameShift] = useState(true);
  const [activeTimeKey, setActiveTimeKey] = useState(null);
  const [draftTimes, setDraftTimes] = useState({
    shift_start: DEFAULT_START,
    shift_end: DEFAULT_END,
  });
  const firstRow = rows[0] || {};
  const sameStart = firstRow.shift_start || DEFAULT_START;
  const sameEnd = firstRow.shift_end || DEFAULT_END;
  const isSelectionMode = mode === 'select';
  const enabledDays = isSelectionMode && available.length > 0
    ? new Set(available.map((row) => row.day_of_week))
    : null;

  const getAvailableRow = (day) =>
    availableByDay.get(day) || {
      day_of_week: day,
      shift_start: DEFAULT_START,
      shift_end: DEFAULT_END,
    };

  useEffect(() => {
    setDraftTimes((prev) => ({
      shift_start: activeTimeKey === 'shift_start' ? prev.shift_start : sameStart,
      shift_end: activeTimeKey === 'shift_end' ? prev.shift_end : sameEnd,
    }));
  }, [activeTimeKey, sameStart, sameEnd]);

  const clampRowToAvailable = (row) => {
    const availableRow = getAvailableRow(row.day_of_week);
    let shiftStart = maxTime(row.shift_start, availableRow.shift_start);
    let shiftEnd = minTime(row.shift_end, availableRow.shift_end);

    if (compareTime(shiftStart, shiftEnd) >= 0) {
      shiftStart = availableRow.shift_start;
      shiftEnd = availableRow.shift_end;
    }

    return {
      ...row,
      shift_start: shiftStart,
      shift_end: shiftEnd,
    };
  };

  const emit = (nextRows) => {
    const normalizedRows = normalizeWorkDaysHours(nextRows);
    onChange(isSelectionMode ? normalizedRows.map(clampRowToAvailable) : normalizedRows);
  };

  const toggleDay = (day) => {
    if (enabledDays && !enabledDays.has(day)) return;

    if (selectedDays.has(day)) {
      emit(rows.filter((row) => row.day_of_week !== day));
      return;
    }

    const availableRow = getAvailableRow(day);
    emit([
      ...rows,
      {
        day_of_week: day,
        shift_start: sameShift ? sameStart : availableRow.shift_start,
        shift_end: sameShift ? sameEnd : availableRow.shift_end,
      },
    ]);
  };

  const updateAllTimes = (key, nextValue) => {
    setDraftTimes((prev) => ({ ...prev, [key]: nextValue }));
    emit(rows.map((row) => ({ ...row, [key]: nextValue })));
  };

  const updateRowTime = (day, key, nextValue) => {
    emit(rows.map((row) => (row.day_of_week === day ? { ...row, [key]: nextValue } : row)));
  };

  const selectAllAvailable = () => {
    if (available.length > 0) emit(available);
  };

  const selectedAvailableRows = rows.map((row) => getAvailableRow(row.day_of_week));
  const commonMinStart = selectedAvailableRows.reduce(
    (current, row) => maxTime(current, row.shift_start),
    '00:00'
  );
  const commonMaxEnd = selectedAvailableRows.reduce(
    (current, row) => minTime(current, row.shift_end),
    '23:59'
  );

  return (
    <div className="wdh">
      <div className="wdh-header">
        <span>{labels.title || (isSelectionMode ? 'Report schedule' : 'Work days and hours')}</span>
        <span className="wdh-summary">
          {isSelectionMode
            ? `${labels.reportWillUse || 'Report will use'}: ${workDaysHoursLabel(rows, labels.noSelection || 'No schedule selected')}`
            : workDaysHoursLabel(rows)}
        </span>
      </div>

      {isSelectionMode && available.length > 0 && (
        <div className="wdh-available">
          <span>{labels.available || 'Available from settings'}: {workDaysHoursLabel(available)}</span>
          <span className="wdh-actions">
            <button type="button" onClick={selectAllAvailable}>
              {labels.selectAll || 'Select all'}
            </button>
            <button type="button" onClick={() => emit([])}>
              {labels.clear || 'Clear'}
            </button>
          </span>
        </div>
      )}

      <div className="wdh-days" role="group" aria-label={labels.days || 'Work days'}>
        {WEEK_DAYS.map((day) => (
          <button
            key={day.value}
            type="button"
            className={`wdh-day ${selectedDays.has(day.value) ? 'is-active' : ''}`}
            onClick={() => toggleDay(day.value)}
            title={day.label}
            disabled={Boolean(enabledDays && !enabledDays.has(day.value))}
          >
            {day.short}
          </button>
        ))}
      </div>

      {!isSelectionMode && rows.length > 0 && (
        <label className="wdh-check">
          <input
            type="checkbox"
            checked={sameShift}
            onChange={(event) => {
              const checked = event.target.checked;
              setSameShift(checked);
              if (checked) {
                emit(rows.map((row) => ({ ...row, shift_start: sameStart, shift_end: sameEnd })));
              }
            }}
          />
          <span>{labels.sameShift || 'Use same shift start / shift end for all work days'}</span>
        </label>
      )}

      {rows.length > 0 && (
        sameShift || isSelectionMode ? (
          <div className="wdh-timeRow">
            <input
              type="time"
              value={activeTimeKey === 'shift_start' ? draftTimes.shift_start : sameStart}
              onFocus={() => {
                setActiveTimeKey('shift_start');
                setDraftTimes((prev) => ({ ...prev, shift_start: sameStart }));
              }}
              onBlur={() => setActiveTimeKey(null)}
              onChange={(event) => updateAllTimes('shift_start', event.target.value)}
              min={isSelectionMode ? commonMinStart : undefined}
              max={isSelectionMode ? commonMaxEnd : undefined}
            />
            <input
              type="time"
              value={activeTimeKey === 'shift_end' ? draftTimes.shift_end : sameEnd}
              onFocus={() => {
                setActiveTimeKey('shift_end');
                setDraftTimes((prev) => ({ ...prev, shift_end: sameEnd }));
              }}
              onBlur={() => setActiveTimeKey(null)}
              onChange={(event) => updateAllTimes('shift_end', event.target.value)}
              min={isSelectionMode ? commonMinStart : undefined}
              max={isSelectionMode ? commonMaxEnd : undefined}
            />
          </div>
        ) : (
          <div className="wdh-list">
            {rows.map((row) => (
              <div className="wdh-row" key={row.day_of_week}>
                <span>{WEEK_DAYS.find((day) => day.value === row.day_of_week)?.label}</span>
                <input
                  type="time"
                  value={row.shift_start}
                  onChange={(event) => updateRowTime(row.day_of_week, 'shift_start', event.target.value)}
                />
                <input
                  type="time"
                  value={row.shift_end}
                  onChange={(event) => updateRowTime(row.day_of_week, 'shift_end', event.target.value)}
                />
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
};

export default WorkDaysHoursPicker;
